/**
 * Ported VERBATIM from `apr_template/src/context/APRContext.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { createContext, useContext, useReducer, type ReactNode, useEffect } from 'react';
import { 
  type APRFrameworkDefinition, 
  type APRModel, 
  type Customer, 
  type Application, 
  type DashboardStats,
  type APRRun,
  type CurrentRunRateData,
  type Dimension
} from './types';
import apiService from './api';

interface APRState {
  framework: APRFrameworkDefinition;
  dimensions: Dimension[];
  customers: Customer[];
  applications: Application[];
  models: APRModel[];
  aprRuns: APRRun[];
  currentRunRateData: CurrentRunRateData[];
  currentCustomer?: Customer;
  currentApplication?: Application;
  currentModel?: APRModel;
  dashboardStats: DashboardStats;
  aprModelStats: {
    totalModels: number;
    createdCount: number;
    scheduledCount: number;
    inProcessCount: number;
    completedCount: number;
  };
  loading: boolean;
}

type APRAction =
  | { type: 'SET_FRAMEWORK'; payload: APRFrameworkDefinition }
  | { type: 'SET_DIMENSIONS'; payload: Dimension[] }
  | { type: 'SET_CUSTOMERS'; payload: Customer[] }
  | { type: 'ADD_CUSTOMER'; payload: Customer }
  | { type: 'UPDATE_CUSTOMER'; payload: Customer }
  | { type: 'DELETE_CUSTOMER'; payload: string }
  | { type: 'SET_CURRENT_CUSTOMER'; payload: Customer }
  | { type: 'SET_APPLICATIONS'; payload: Application[] }
  | { type: 'ADD_APPLICATION'; payload: Application }
  | { type: 'UPDATE_APPLICATION'; payload: Application }
  | { type: 'DELETE_APPLICATION'; payload: string }
  | { type: 'SET_CURRENT_APPLICATION'; payload: Application }
  | { type: 'SET_MODELS'; payload: APRModel[] }
  | { type: 'ADD_MODEL'; payload: APRModel }
  | { type: 'UPDATE_MODEL'; payload: APRModel }
  | { type: 'DELETE_MODEL'; payload: string }
  | { type: 'SET_CURRENT_MODEL'; payload: APRModel }
  | { type: 'SET_APR_RUNS'; payload: APRRun[] }
  | { type: 'ADD_APR_RUN'; payload: APRRun }
  | { type: 'UPDATE_APR_RUN'; payload: APRRun }
  | { type: 'DELETE_APR_RUN'; payload: string }
  | { type: 'SET_CURRENT_RUN_RATE_DATA'; payload: CurrentRunRateData[] }
  | { type: 'CLEAR_CURRENT_RUN_RATE_DATA' }
  | { type: 'UPDATE_DASHBOARD_STATS' }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: APRState = {
  framework: {
    id: 'framework',
    name: 'APR Framework',
    version: '1.0',
    dimensions: []
  },
  dimensions: [],
  customers: [],
  applications: [],
  models: [],
  aprRuns: [],
  currentRunRateData: [],
  dashboardStats: {
    totalApplications: 0,
    totalModels: 0,
    recommendations: {
      'Retire': 0,
      'Re-Platform': 0,
      'Re-Design': 0,
      'Retain': 0
    },
    totalCosts: {
      'current_run_rate': 0,
      'planned_cost': 0,
      'migration_conversion': 0,
      'future_run_rate': 0
    }
  },
  aprModelStats: {
    totalModels: 0,
    createdCount: 0,
    scheduledCount: 0,
    inProcessCount: 0,
    completedCount: 0
  },
  loading: true
};

const calculateAPRModelStats = (models: APRModel[]) => {
  const stats = {
    totalModels: models.length,
    createdCount: 0,
    scheduledCount: 0,
    inProcessCount: 0,
    completedCount: 0
  };

  models.forEach(model => {
    switch (model.status) {
      case 'Created':
        stats.createdCount++;
        break;
      case 'Scheduled':
        stats.scheduledCount++;
        break;
      case 'In-Process':
        stats.inProcessCount++;
        break;
      case 'Completed':
        stats.completedCount++;
        break;
    }
  });

  return stats;
};

const calculateDashboardStats = (state: APRState): DashboardStats => {
  const recommendations = state.applications.reduce((acc, app) => {
    if (app.recommendation) {
      acc[app.recommendation] = (acc[app.recommendation] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  // Calculate current run rate from actual current run rate data (exclude parent rows to match Current Run Rate screen)
  const currentRunRateTotal = state.currentRunRateData.reduce((sum, item) => {
    if ((item as any).isParent || (item as any).isParentSummary) return sum;
    return sum + (parseFloat(String(item.totalAnnualCost)) || 0);
  }, 0);

  // Calculate other costs from applications
  const otherCosts = state.applications.reduce((acc, app) => {
    app.costs.forEach(cost => {
      if (cost.type !== 'current_run_rate') {
        acc[cost.type] = (acc[cost.type] || 0) + cost.amount;
      }
    });
    return acc;
  }, {
    'planned_cost': 0,
    'migration_conversion': 0,
    'future_run_rate': 0
  });

  // Combine current run rate with other costs
  const totalCosts = {
    'current_run_rate': currentRunRateTotal,
    ...otherCosts
  };

  return {
    totalApplications: state.applications.length,
    totalModels: state.models.length,
    recommendations: {
      'Retire': recommendations['Retire'] || 0,
      'Re-Platform': recommendations['Re-Platform'] || 0,
      'Re-Design': recommendations['Re-Design'] || 0,
      'Retain': recommendations['Retain'] || 0
    },
    totalCosts
  };
};

const aprReducer = (state: APRState, action: APRAction): APRState => {
  switch (action.type) {
    case 'SET_FRAMEWORK':
      return { ...state, framework: action.payload };
    
    case 'SET_DIMENSIONS':
      return { 
        ...state, 
        dimensions: action.payload,
        framework: { 
          ...state.framework, 
          dimensions: action.payload 
        }
      };
    
    case 'SET_CUSTOMERS':
      return { 
        ...state, 
        customers: action.payload,
        dashboardStats: calculateDashboardStats({ ...state, customers: action.payload })
      };
    
    case 'SET_APPLICATIONS':
      return { 
        ...state, 
        applications: action.payload,
        dashboardStats: calculateDashboardStats({ ...state, applications: action.payload })
      };
    
    case 'SET_MODELS':
      const newState = { 
        ...state, 
        models: action.payload
      };
      return {
        ...newState,
        dashboardStats: calculateDashboardStats(newState),
        aprModelStats: calculateAPRModelStats(action.payload)
      };
    
    case 'SET_APR_RUNS':
      return { 
        ...state, 
        aprRuns: action.payload
      };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'ADD_CUSTOMER':
      const newCustomersAdd = [...state.customers, action.payload];
      return { 
        ...state, 
        customers: newCustomersAdd,
        dashboardStats: calculateDashboardStats({ ...state, customers: newCustomersAdd })
      };
    
    case 'UPDATE_CUSTOMER':
      const updatedCustomers = state.customers.map(customer =>
        customer.id === action.payload.id ? action.payload : customer
      );
      return { 
        ...state, 
        customers: updatedCustomers,
        dashboardStats: calculateDashboardStats({ ...state, customers: updatedCustomers })
      };
    
    case 'DELETE_CUSTOMER':
      const remainingCustomers = state.customers.filter(customer => customer.id !== action.payload);
      const remainingApplications = state.applications.filter(app => app.customerId !== action.payload);
      return { 
        ...state, 
        customers: remainingCustomers,
        applications: remainingApplications,
        dashboardStats: calculateDashboardStats({ 
          ...state, 
          customers: remainingCustomers, 
          applications: remainingApplications 
        })
      };
    
    case 'SET_CURRENT_CUSTOMER':
      return { ...state, currentCustomer: action.payload };
    
    case 'ADD_APPLICATION':
      const newApplicationsAdd = [...state.applications, action.payload];
      return { 
        ...state, 
        applications: newApplicationsAdd,
        dashboardStats: calculateDashboardStats({ ...state, applications: newApplicationsAdd })
      };
    
    case 'UPDATE_APPLICATION':
      const updatedApplications = state.applications.map(app =>
        app.id === action.payload.id ? action.payload : app
      );
      return { 
        ...state, 
        applications: updatedApplications,
        dashboardStats: calculateDashboardStats({ ...state, applications: updatedApplications })
      };
    
    case 'DELETE_APPLICATION':
      const remainingApps = state.applications.filter(app => app.id !== action.payload);
      return { 
        ...state, 
        applications: remainingApps,
        dashboardStats: calculateDashboardStats({ ...state, applications: remainingApps })
      };
    
    case 'SET_CURRENT_APPLICATION':
      return { ...state, currentApplication: action.payload };
    
    case 'ADD_MODEL':
      const newModelsAdd = [...state.models, action.payload];
      const newStateAdd = { 
        ...state, 
        models: newModelsAdd
      };
      return { 
        ...newStateAdd,
        dashboardStats: calculateDashboardStats(newStateAdd),
        aprModelStats: calculateAPRModelStats(newModelsAdd)
      };
    
    case 'UPDATE_MODEL':
      const updatedModels = state.models.map(model =>
        model.id === action.payload.id ? action.payload : model
      );
      const newStateUpdate = { 
        ...state, 
        models: updatedModels
      };
      return { 
        ...newStateUpdate,
        dashboardStats: calculateDashboardStats(newStateUpdate),
        aprModelStats: calculateAPRModelStats(updatedModels)
      };
    
    case 'DELETE_MODEL':
      const remainingModels = state.models.filter(model => model.id !== action.payload);
      const newStateDelete = { 
        ...state, 
        models: remainingModels
      };
      return { 
        ...newStateDelete,
        dashboardStats: calculateDashboardStats(newStateDelete),
        aprModelStats: calculateAPRModelStats(remainingModels)
      };
    
    case 'SET_CURRENT_MODEL':
      return { ...state, currentModel: action.payload };
    
    case 'ADD_APR_RUN':
      return { 
        ...state, 
        aprRuns: [...state.aprRuns, action.payload]
      };
    
    case 'UPDATE_APR_RUN':
      return { 
        ...state, 
        aprRuns: state.aprRuns.map(run =>
          run.id === action.payload.id ? action.payload : run
        )
      };
    
    case 'DELETE_APR_RUN':
      return { 
        ...state, 
        aprRuns: state.aprRuns.filter(run => run.id !== action.payload)
      };
    
    case 'SET_CURRENT_RUN_RATE_DATA':
      const newStateWithRunRate = {
        ...state,
        currentRunRateData: action.payload
      };
      return {
        ...newStateWithRunRate,
        dashboardStats: calculateDashboardStats(newStateWithRunRate)
      };
    
    case 'CLEAR_CURRENT_RUN_RATE_DATA':
      return {
        ...state,
        currentRunRateData: []
      };
    
    case 'UPDATE_DASHBOARD_STATS':
      return { 
        ...state, 
        dashboardStats: calculateDashboardStats(state)
      };
    
    default:
      return state;
  }
};

const APRContext = createContext<{
  state: APRState;
  dispatch: React.Dispatch<APRAction>;
  refreshData: () => Promise<void>;
} | null>(null);

export const APRProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(aprReducer, initialState);

  const loadData = async () => {
    console.log('🚀 APRContext: Starting loadData...');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // Load all data from API using Promise.allSettled to handle partial failures
      // This ensures that if one API fails (e.g., due to auth), others still load
      console.log('🔄 APRContext: Calling all APIs...');

      const results = await Promise.allSettled([
        apiService.getDimensions(),
        apiService.getCustomers(),
        apiService.getApplications(),
        apiService.getModels(),
        apiService.getAPRRuns(),
        apiService.getCurrentRunRateData()
      ]);

      // Extract data from settled promises, using empty arrays for failures
      const dimensions = results[0].status === 'fulfilled' ? results[0].value : [];
      const customers = results[1].status === 'fulfilled' ? results[1].value : [];
      const applications = results[2].status === 'fulfilled' ? results[2].value : [];
      const models = results[3].status === 'fulfilled' ? results[3].value : [];
      const aprRuns = results[4].status === 'fulfilled' ? results[4].value : [];
      const currentRunRateData = results[5].status === 'fulfilled' ? results[5].value : [];

      // Log any failures
      results.forEach((result, index) => {
        const apiNames = ['Dimensions', 'Customers', 'Applications', 'Models', 'APRRuns', 'CurrentRunRateData'];
        if (result.status === 'rejected') {
          console.warn(`⚠️ APRContext: ${apiNames[index]} API failed:`, result.reason?.message || result.reason);
        }
      });

      console.log('✅ APRContext: APIs returned (some may have failed)');
      console.log('📊 APRContext: Applications loaded:', applications?.length || 0);
      console.log('📊 APRContext: Dimensions loaded:', dimensions?.length || 0);
      console.log('📊 APRContext: Customers loaded:', customers?.length || 0);
      console.log('📊 APRContext: Models loaded:', models?.length || 0);

      dispatch({ type: 'SET_DIMENSIONS', payload: dimensions });
      dispatch({ type: 'SET_CUSTOMERS', payload: customers });
      dispatch({ type: 'SET_APPLICATIONS', payload: applications });
      dispatch({ type: 'SET_MODELS', payload: models });
      dispatch({ type: 'SET_APR_RUNS', payload: aprRuns });
      dispatch({ type: 'SET_CURRENT_RUN_RATE_DATA', payload: currentRunRateData || [] });

      console.log('✅ APRContext: All dispatches completed');
    } catch (error) {
      console.error('❌ APRContext: Error loading data:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      console.log('🏁 APRContext: loadData finished');
    }
  };

  const refreshData = async () => {
    await loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <APRContext.Provider value={{ state, dispatch, refreshData }}>
      {children}
    </APRContext.Provider>
  );
};

export const useAPR = () => {
  const context = useContext(APRContext);
  if (!context) {
    throw new Error('useAPR must be used within an APRProvider');
  }
  return context;
};